import { useToast } from "@/providers";
import { UseManageFormOptions } from "./types";

export function useManageForm<T extends { id?: string }, R = void>({
  createFn,
  updateFn,
  successMessage = {
    create: "Item successfully created",
    update: "Item successfully updated",
  },
  errorMessage = "Something went wrong. Please try again later.",
}: UseManageFormOptions<T, R>) {
  const { showToast } = useToast();

  const showSuccessToast = (isEditing: boolean) =>
    showToast({
      severity: "success",
      summary: "Success",
      detail: isEditing ? successMessage.update : successMessage.create,
      life: 3000,
    });

  const showErrorToast = () =>
    showToast({
      severity: "error",
      summary: "Error",
      detail: errorMessage,
      life: 3000,
    });

  const updateItem = async (id: string, data: T) => {
    if (!updateFn) throw new Error("updateFn is not defined");
    const res = await updateFn(id, { ...data });
    showSuccessToast(true);
    return res;
  };

  const createItem = async (data: T) => {
    if (!createFn) throw new Error("createFn is not defined");
    const res = await createFn(data);
    showSuccessToast(false);
    return res;
  };

  const sendForm = async (values: T): Promise<R> => {
    try {
      if (values.id) {
        const { id, ...data } = values;
        return await updateItem(id!, data as T);
      } else {
        return await createItem(values);
      }
    } catch (err) {
      console.error("Error while sending form:", err);
      showErrorToast();
      throw err;
    }
  };

  return { sendForm };
}
